import { useMemo } from "react";

import { resolveChartColor } from "./chart-colors";
import { createOverlayPlugin } from "./chart-overlay";
import { resolveVerticalScale } from "./chart-scale";
import type { HomeChartProps, HomeChartViewModel } from "./types-home-chart";

/** Tensão do Chart.js na curva suavizada do Capex. */
const SUAVIZACAO = 0.4;

export function useHomeChart({
  points,
  formatValue,
  formatPointLabel = formatValue,
  showPointLabels = false,
  showAxisLabels = false,
  baseline = "zero",
  borderWidth = 3,
  markerIndex,
  showGrid = true,
  suave = false,
  testId,
}: HomeChartProps): HomeChartViewModel {
  const data = useMemo(() => {
    const line = resolveChartColor("line");

    return {
      labels: points.map((point) => point.label),
      datasets: [
        {
          data: points.map((point) => point.value),
          borderColor: line,
          borderWidth,
          // A área sob a curva é o que dá volume ao gráfico do protótipo; o véu
          // translúcido mantém a fotografia visível ao fundo do cartão.
          backgroundColor: resolveChartColor("fill"),
          fill: "origin",
          tension: suave ? SUAVIZACAO : 0,
          pointRadius: 0,
          pointHoverRadius: 4,
          pointHoverBackgroundColor: line,
        },
      ],
    };
  }, [borderWidth, points, suave]);

  const options = useMemo(() => {
    const axisColor = resolveChartColor("text");
    const values = points.map((point) => point.value);
    const scale = resolveVerticalScale(values, baseline);

    return {
      responsive: true,
      maintainAspectRatio: false,
      // Espaço no topo e nas laterais para os valores escritos sobre a curva
      // não serem cortados nos pontos das pontas.
      layout: {
        padding: showPointLabels ? { top: 22, left: 20, right: 20 } : { top: 12 },
      },
      interaction: { mode: "index" as const, intersect: false },
      plugins: {
        legend: { display: false },
        tooltip: {
          displayColors: false,
          callbacks: {
            label: (item: { parsed: { y: number } }) => formatValue(item.parsed.y),
          },
        },
      },
      scales: {
        x: {
          // O protótipo marca cada rótulo com uma linha vertical tênue.
          grid: { display: false },
          border: { display: false },
          ticks: { display: showAxisLabels, color: axisColor, font: { size: 11 } },
        },
        y: {
          min: scale.min,
          max: scale.max,
          grid: { display: false },
          border: { display: false },
          ticks: { display: false },
        },
      },
    };
  }, [baseline, formatValue, points, showAxisLabels, showPointLabels]);

  const plugins = useMemo(
    () => [
      createOverlayPlugin({
        points,
        formatPointLabel,
        showPointLabels,
        markerIndex,
        gridColor: resolveChartColor("grid"),
        showAxisLabels,
        showGrid,
      }),
    ],
    [formatPointLabel, markerIndex, points, showAxisLabels, showGrid, showPointLabels],
  );

  const rows = points.map((point) => ({ label: point.label, value: formatValue(point.value) }));

  return { data, options, plugins, rows, dadosTestId: `${testId}-dados` };
}
